import React from 'react';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  ChipsCell,
  ChipsCellComponentProps,
  ChipsSkeleton,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  DropdownButtonSkeleton,
  FilterItems,
  MenuItemProps,
  getFixedWidthFaIcon,
} from '@odin-labs/components';
import {
  ensureNonEmptyItems,
  getFormattedPhoneNumber,
  getInitialsForUser,
} from 'utils';
import { faUserTimes } from 'components/icons';
import {
  ContractorMember,
  ContractorUserColumn,
  ContractorUsersFilters,
} from 'containers/contractor/types';
import { AuthUser } from 'auth/types';
import { to } from 'acl';
import {
  DateTimeCell,
  DateTimeCellData,
} from 'containers/jobsiteAccessActivity/helpers/cells';
import { GetUsersOrderByField } from 'apollo/generated/client-operations';

const UserTimesIcon = getFixedWidthFaIcon({ icon: faUserTimes });

export const getColumns = ({
  user,
  contractorId,
  openRemoveContractorUserModal,
}: {
  user: AuthUser;
  contractorId: string;
  openRemoveContractorUserModal: (contractorMemberId: string) => void;
}): ContractorUserColumn[] => {
  const canRemoveContractorUsers = user.isAllowed(
    to.removeContractorUsers,
    contractorId,
  );
  const isActionsButtonVisible = canRemoveContractorUsers;

  return ensureNonEmptyItems<ContractorUserColumn>([
    {
      id: 'user',
      Header: 'User',
      accessor: (contractorMember: ContractorMember): string => {
        const { firstName, lastName } = contractorMember.user.identity ?? {};
        return [firstName, lastName].join(' ');
      },
      Cell: AvatarCell,
      CellLoading: <AvatarSkeleton size="xs" hideDetails />,
      componentProps: (
        contractorMember: ContractorMember,
      ): AvatarCellComponentProps => ({
        src: contractorMember.user?.worker?.profilePictureCropped?.downloadUrl,
        placeholder: getInitialsForUser(contractorMember.user),
        objectFit: 'cover',
        size: 'xs',
      }),
    },
    {
      id: 'email',
      Header: <>Email</>,
      accessor: (contractorMember: ContractorMember): string =>
        contractorMember.user.identity.email,
    },
    {
      id: 'phoneNumber',
      Header: 'Phone Number',
      accessor: (contractorMember: ContractorMember): string =>
        getFormattedPhoneNumber(contractorMember.user.identity.phoneNumber),
    },
    {
      id: 'role',
      Header: 'Role',
      // cellClassName: cn('odin-whitespace-pre-wrap'),
      accessor: (): string[] => [],
      Cell: ChipsCell,
      CellLoading: ChipsSkeleton,
      componentProps: (
        contractorMember: ContractorMember,
      ): ChipsCellComponentProps => ({
        chips: contractorMember.user.roles.map((role) => ({
          text: role.object.objectDetails.name,
          secondaryText: role.displayName,
          withDot: false,
        })),
      }),
      disableSortBy: true,
    },
    {
      id: 'lastSessionDate',
      Header: 'Last Session',
      accessor: (contractorMember: ContractorMember): DateTimeCellData => {
        const { createdAt } = contractorMember.user.lastSession ?? {};
        const timeZoneName = ''; // moment.tz(createdAt, timeZone).zoneAbbr();
        return createdAt && { date: new Date(createdAt), timeZoneName };
      },
      Cell: DateTimeCell,
    },
    {
      id: 'lastSessionVersion',
      Header: 'Odin version',
      accessor: (contractorMember: ContractorMember): string =>
        contractorMember.user.lastSession?.appVersion,
    },
    isActionsButtonVisible && {
      Header: '',
      id: 'actions',
      Cell: DropdownButtonCell,
      CellLoading: (): React.ReactElement => (
        <DropdownButtonSkeleton cellAlignment="right" />
      ),
      componentProps: (
        contractorMember: ContractorMember,
      ): DropdownButtonCellComponentProps => ({
        cellAlignment: 'right',
        menuItems: ensureNonEmptyItems<MenuItemProps>([
          canRemoveContractorUsers && {
            icon: UserTimesIcon,
            text: 'Remove User Role',
            theme: 'danger',
            onClick: (): void =>
              openRemoveContractorUserModal(contractorMember.id),
          },
        ]),
      }),
    },
  ]);
};

export const getFilterItems = (): FilterItems<ContractorUsersFilters> => ({});

export const orderByFields: Record<string, GetUsersOrderByField> = {
  user: GetUsersOrderByField.Name,
  email: GetUsersOrderByField.Email,
  phoneNumber: GetUsersOrderByField.PhoneNumber,
  lastSessionDate: GetUsersOrderByField.LastActiveAt,
  lastSessionVersion: GetUsersOrderByField.AppVersion,
};
